import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { CameraPermissionPrompt } from '../components/import/CameraPermissionPrompt';
import { QRScanner } from '../components/import/QRScanner';
import { LookupLoadingState } from '../components/import/LookupLoadingState';
import { ImportReviewScreen } from '../components/import/ImportReviewScreen';
import { ScanErrorPanel } from '../components/import/ScanErrorPanel';
import { mapCoaToIngredientForm } from '../utils/coaMapper';
import { lookupLucidGreenProduct } from '../graphql/ingredients';
import type { ImportState, ImportError, LucidGreenProductData } from '../types/import';
import type { IngredientFormData } from '../types/ingredient';

const client = generateClient();
const MAX_RETRIES = 3;
const LOOKUP_TIMEOUT_MS = 10_000;

/**
 * Classify a GraphQL/network error into an ImportError type and message.
 * Lambda errors surface through AppSync as GraphQL errors with specific messages.
 */
function classifyLookupError(error: unknown): { type: ImportError['type']; message: string } {
  // GraphQL errors from AppSync (includes Lambda errors that propagate through resolvers)
  if (error && typeof error === 'object' && 'errors' in error) {
    const gqlErrors = (error as { errors: Array<{ message?: string }> }).errors;
    const firstMessage = gqlErrors?.[0]?.message?.toLowerCase() ?? '';

    if (firstMessage.includes('timed out')) {
      return { type: 'timeout', message: 'The request timed out.' };
    }
    if (firstMessage.includes('network error')) {
      return { type: 'network', message: "Network issue — couldn't reach the product database." };
    }
    if (firstMessage.includes('returned status')) {
      // Lambda caught a non-2xx response from LucidGreen API
      return { type: 'api-error', message: "Couldn't retrieve product data for this code." };
    }
    // Generic AppSync/GraphQL error (auth, schema, resolver issues)
    return { type: 'appsync-error', message: 'Connection issue — please try again.' };
  }

  // AbortError from our client-side timeout
  if (error instanceof DOMException && error.name === 'AbortError') {
    return { type: 'timeout', message: 'The request timed out.' };
  }

  // Generic network error (fetch failed, no internet, etc.)
  if (error instanceof TypeError) {
    return { type: 'network', message: "Network issue — couldn't reach the product database." };
  }

  // Default to network error for unknown failures
  return { type: 'network', message: "Network issue — couldn't reach the product database." };
}

/**
 * Determine whether an error type supports retries.
 * Only network, timeout, and appsync-error types are retryable.
 */
function isRetryableErrorType(type: ImportError['type']): boolean {
  return type === 'network' || type === 'timeout' || type === 'appsync-error';
}

export function ImportPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<ImportState>({ step: 'permission-prompt' });
  const [retryCount, setRetryCount] = useState(0);
  // Store the last lucidId so we can retry the lookup without re-scanning
  const lastLucidIdRef = useRef<string | null>(null);
  const lastPartialDataRef = useRef<IngredientFormData | null>(null);

  const handlePermissionGranted = useCallback(() => {
    setState({ step: 'scanning' });
  }, []);

  const handleFallback = useCallback(() => {
    const partialData = lastPartialDataRef.current;
    if (partialData) {
      navigate('/ingredients/new', { state: { prePopulatedData: partialData } });
    } else {
      navigate('/ingredients/new');
    }
  }, [navigate]);

  const performLookup = useCallback(
    async (lucidId: string, currentRetryCount: number) => {
      setState({ step: 'looking-up', lucidId });
      lastLucidIdRef.current = lucidId;

      // Set up client-side timeout as a safety net
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

      try {
        const response = await client.graphql({
          query: lookupLucidGreenProduct,
          variables: { lucidId },
        });

        clearTimeout(timeoutId);

        const data = (
          response as { data: { lookupLucidGreenProduct: LucidGreenProductData | null } }
        ).data.lookupLucidGreenProduct;

        // Missing product data — all ID fields null/empty
        if (!data || (!data.productName && !data.brand && !data.strain)) {
          // Capture any cannabinoid data as partial for fallback
          if (data) {
            const { formData } = mapCoaToIngredientForm(data);
            const hasAnyData = Object.values(formData).some((v) => v !== '');
            if (hasAnyData) {
              lastPartialDataRef.current = formData;
            }
          }
          setState({
            step: 'error',
            error: {
              type: 'no-product-data',
              message: 'No product information found for this code.',
              retryCount: currentRetryCount,
              canRetry: false,
            },
          });
          return;
        }

        const { formData, autoPopulatedFields } = mapCoaToIngredientForm(data);
        lastPartialDataRef.current = formData;
        setState({ step: 'review', formData, autoPopulatedFields });
        // Reset retry count on successful lookup
        setRetryCount(0);
      } catch (error: unknown) {
        clearTimeout(timeoutId);

        const { type, message } = classifyLookupError(error);
        const retryable = isRetryableErrorType(type);

        // Only increment retry count for retryable errors
        const newRetryCount = retryable ? currentRetryCount + 1 : currentRetryCount;
        const canRetry = retryable && newRetryCount < MAX_RETRIES;

        if (retryable) {
          setRetryCount(newRetryCount);
        }

        setState({
          step: 'error',
          error: {
            type,
            message,
            retryCount: newRetryCount,
            canRetry,
          },
        });
      }
    },
    []
  );

  const handleLucidIdDecoded = useCallback(
    async (lucidId: string) => {
      await performLookup(lucidId, retryCount);
    },
    [performLookup, retryCount]
  );

  const handleInvalidQr = useCallback((payload: string) => {
    setState({
      step: 'error',
      error: {
        type: 'invalid-qr',
        message: `This doesn't look like a LucidGreen product code: ${payload}`,
        retryCount: 0,
        canRetry: true,
      },
    });
  }, []);

  const handleTimeout = useCallback(() => {
    setState({
      step: 'error',
      error: {
        type: 'timeout',
        message:
          'Having trouble detecting a code? Try repositioning the product label or improving lighting.',
        retryCount: 0,
        canRetry: true,
      },
    });
  }, []);

  /** "Scan Again" — starts a fresh scan session, resets retry counter */
  const handleScanAgain = useCallback(() => {
    setRetryCount(0);
    lastLucidIdRef.current = null;
    lastPartialDataRef.current = null;
    setState({ step: 'scanning' });
  }, []);

  /** "Retry" — re-attempts the lookup with the same lucidId, preserves retry count */
  const handleRetry = useCallback(() => {
    const lucidId = lastLucidIdRef.current;
    if (lucidId) {
      performLookup(lucidId, retryCount);
    } else {
      // Fallback: if no lucidId stored, go back to scanning
      setState({ step: 'scanning' });
    }
  }, [performLookup, retryCount]);

  const handleReviewCancel = useCallback(() => {
    setState({ step: 'scanning' });
  }, []);

  const handleReviewSaved = useCallback(
    (ingredientId: string) => {
      navigate(`/ingredients/${ingredientId}`);
    },
    [navigate]
  );

  const handleScannerCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="page import-page">
      <h2>Import from Product</h2>
      <p className="import-instructions">
        Scan a LucidGreen QR code found on your cannabis product packaging to automatically
        populate ingredient data (strain name, brand, and cannabinoid percentages).
      </p>

      {state.step === 'permission-prompt' && (
        <CameraPermissionPrompt
          onPermissionGranted={handlePermissionGranted}
          onFallback={handleFallback}
        />
      )}

      {state.step === 'scanning' && (
        <>
          <QRScanner
            onLucidIdDecoded={handleLucidIdDecoded}
            onInvalidQr={handleInvalidQr}
            onTimeout={handleTimeout}
            onCancel={handleScannerCancel}
          />
          <div className="import-manual-entry">
            <button
              className="cta-button-secondary"
              onClick={handleFallback}
              type="button"
            >
              Enter Manually
            </button>
          </div>
        </>
      )}

      {state.step === 'looking-up' && <LookupLoadingState />}

      {state.step === 'review' && (
        <ImportReviewScreen
          formData={state.formData}
          autoPopulatedFields={state.autoPopulatedFields}
          onCancel={handleReviewCancel}
          onSaved={handleReviewSaved}
        />
      )}

      {state.step === 'error' && (
        <ScanErrorPanel
          error={state.error}
          onScanAgain={handleScanAgain}
          onRetry={handleRetry}
          onFallback={handleFallback}
        />
      )}
    </div>
  );
}
